// ---------------------------------------------------------------------------
// Filter-options derivation for the Access Legitimacy events grid (design v6
// §3.3). Pure mapping from the live envelope → the view-model option lists the
// filter molecule renders, replacing the hardcoded story constants.
//
//  - Sensitivity options come from `sensitivityCounts`: a reserved "All
//    Sensitivities" first (carrying `all`), then one option per catalogued
//    label in catalog order (JS preserves the backend's insertion order), each
//    with its faceted count + humanized label. A catalogued level with a zero
//    count still appears; the backend keys level-less events as `unclassified`
//    (TDEV-1132), so they get an "Unclassified" option like any other level.
//  - User-type / file-type options come from `filters`, humanized, with the
//    badge / icon resolved (an unrecognized file-type label simply renders no
//    icon — the dropdown tolerates a missing glyph).
// ---------------------------------------------------------------------------

import type { AccessLegitimacyEnvelope } from '@dawn/feature-config';
import {
  FileTypeIcon,
  UserTypeBadge,
  humanizeLabel,
  resolveFileTypeIcon,
  resolveUserTypeBadge,
  type FilterDropdownOption,
  type SensitivityFilterOption,
} from '@dawn/ui';

export interface AccessLegitimacyFilterOptions {
  sensitivityOptions: SensitivityFilterOption[];
  userTypeOptions: FilterDropdownOption[];
  fileTypeOptions: FilterDropdownOption[];
}

export function deriveFilterOptions(
  envelope: AccessLegitimacyEnvelope
): AccessLegitimacyFilterOptions {
  const { all, ...catalogued } = envelope.sensitivityCounts;

  const sensitivityOptions: SensitivityFilterOption[] = [
    { value: 'all', label: 'All Sensitivities', count: all },
    ...Object.entries(catalogued).map(([label, count]) => ({
      value: label,
      label: humanizeLabel(label),
      count,
    })),
  ];

  const userTypeOptions: FilterDropdownOption[] = envelope.filters.userTypes.map((label) => {
    const badge = resolveUserTypeBadge(label);
    return {
      value: label,
      label: humanizeLabel(label),
      icon: (
        <UserTypeBadge
          size="sm"
          type={badge.type}
          isPrivileged={badge.isExternal}
          aria-label={humanizeLabel(label)}
        />
      ),
    };
  });

  const fileTypeOptions: FilterDropdownOption[] = envelope.filters.fileTypes.map((label) => {
    const iconName = resolveFileTypeIcon(label);
    return {
      value: label,
      label: humanizeLabel(label),
      ...(iconName !== null ? { icon: <FileTypeIcon name={iconName} size={14} /> } : {}),
    };
  });

  return { sensitivityOptions, userTypeOptions, fileTypeOptions };
}
